import React, { useState, useEffect, useMemo } from 'react';
import './CityProfile.css';
import API_BASE_URL from './config';
import DataTable from './components/DataTable/DataTable';
import { RFQ_COLUMNS } from './constants/tableColumns';
import { WORK_TYPES, JOB_STATUSES, normalizeStatus } from './constants';

function CityProfile({ cityName, onBack, onViewJobs, onSelectJob, theme, toggleTheme }) {
  const [cityData, setCityData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  
  // Journal states
  const [journalEntries, setJournalEntries] = useState([]);
  const [newJournalEntry, setNewJournalEntry] = useState('');
  const [userName, setUserName] = useState(localStorage.getItem('rfq_user_name') || 'User');
  
  // City contacts state
  const [cityContacts, setCityContacts] = useState([]);
  
  // City links state (Sprint 7)
  const [cityLinks, setCityLinks] = useState([]);
  const [showAddLink, setShowAddLink] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [newLink, setNewLink] = useState({
    link_title: '',
    link_url: '',
    link_type: 'general',
    notes: ''
  });
  
  // Manual job entry state (Sprint 7)
  const [showManualJobEntry, setShowManualJobEntry] = useState(false);
  const [manualJobText, setManualJobText] = useState('');
  const [manualJobUrl, setManualJobUrl] = useState('');
  
  // Scraper config state (Intelligent Scraper - Phase 1)
  const [scraperConfig, setScraperConfig] = useState(null);
  const [showScraperConfig, setShowScraperConfig] = useState(false);
  const [editingScraperConfig, setEditingScraperConfig] = useState(false);
  const [scraperConfigForm, setScraperConfigForm] = useState({
    url: '',
    manual: false,
    row_selector: '',
    cell_count: 4,
    is_dynamic: false,
    pre_scrape_actions: '',
    scraper_notes: ''
  });
  
  // Filters for jobs list
  const [filters, setFilters] = useState({
    workType: 'all',
    userStatus: 'all',
    hideIgnored: true,
    sortBy: 'due_date'
  });

  useEffect(() => {
    fetchCityProfile();
    fetchCityJobs();
    fetchCityJournal();
    fetchCityContacts();
    fetchCityLinks();
    fetchScraperConfig();
  }, [cityName]);

  const fetchCityProfile = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/city_profile/${encodeURIComponent(cityName)}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCityData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching city profile:', error);
      setLoading(false);
    }
  };

  const fetchCityJobs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rfqs`, {
        credentials: 'include'
      });
      const allJobs = await response.json();
      // Filter jobs for this city
      const cityJobs = allJobs.filter(job => job.organization === cityName);
      setJobs(cityJobs);
    } catch (error) {
      console.error('Error fetching city jobs:', error);
    }
  };

  const fetchCityJournal = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/city_journal/${encodeURIComponent(cityName)}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setJournalEntries(data);
    } catch (error) {
      console.error('Error fetching city journal:', error);
    }
  };

  const fetchCityContacts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/city/${encodeURIComponent(cityName)}/contacts`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCityContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching city contacts:', error);
      setCityContacts([]);
    }
  };

  // Sprint 7: City Links Functions
  const fetchCityLinks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/city/${encodeURIComponent(cityName)}/links`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCityLinks(data.links || []);
    } catch (error) {
      console.error('Error fetching city links:', error);
      setCityLinks([]);
    }
  };

  const addCityLink = async () => {
    if (!newLink.link_title.trim() || !newLink.link_url.trim()) {
      alert('Please provide both a title and URL');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/city/${encodeURIComponent(cityName)}/links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newLink)
      });
      const data = await response.json();
      
      if (data.error) {
        alert('Error adding link: ' + data.error);
      } else {
        setNewLink({ link_title: '', link_url: '', link_type: 'general', notes: '' });
        setShowAddLink(false);
        fetchCityLinks();
      }
    } catch (error) {
      console.error('Error adding city link:', error);
      alert('Failed to add link');
    }
  };

  const startEditLink = (link) => {
    setEditingLinkId(link.id);
    setNewLink({
      link_title: link.link_title,
      link_url: link.link_url,
      link_type: link.link_type,
      notes: link.notes || ''
    });
    setShowAddLink(true);
  };

  const updateCityLink = async () => {
    if (!newLink.link_title.trim() || !newLink.link_url.trim()) {
      alert('Please provide both a title and URL');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/city/links/${editingLinkId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newLink)
      });
      const data = await response.json();
      
      if (data.error) {
        alert('Error updating link: ' + data.error);
      } else {
        setNewLink({ link_title: '', link_url: '', link_type: 'general', notes: '' });
        setShowAddLink(false);
        setEditingLinkId(null);
        fetchCityLinks();
      }
    } catch (error) {
      console.error('Error updating city link:', error);
      alert('Failed to update link');
    }
  };

  const cancelEditLink = () => {
    setEditingLinkId(null);
    setNewLink({ link_title: '', link_url: '', link_type: 'general', notes: '' });
    setShowAddLink(false);
  };

  const deleteCityLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;

    try {
      await fetch(`${API_BASE_URL}/city/links/${linkId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchCityLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      alert('Failed to delete link');
    }
  };

  // Sprint 7: Manual Job Entry Function
  const addManualJob = async () => {
    if (!manualJobText.trim()) {
      alert('Please enter job details');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/city/${encodeURIComponent(cityName)}/add_manual_job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_text: manualJobText,
          link: manualJobUrl.trim() || null
        })
      });
      const data = await response.json();
      
      if (data.error) {
        alert('Error adding job: ' + data.error);
      } else {
        alert('Job added successfully!');
        setManualJobText('');
        setManualJobUrl('');
        setShowManualJobEntry(false);
        fetchCityJobs(); // Refresh the jobs list
      }
    } catch (error) {
      console.error('Error adding manual job:', error);
      alert('Failed to add job');
    }
  };

  // Intelligent Scraper: Fetch scraper configuration
  const fetchScraperConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/city/${encodeURIComponent(cityName)}/scraper_config`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (!data.error) {
        setScraperConfig(data);
        setScraperConfigForm({
          url: data.url || '',
          manual: data.manual || false,
          row_selector: data.row_selector || '',
          cell_count: data.cell_count || 4,
          is_dynamic: data.is_dynamic || false,
          pre_scrape_actions: data.pre_scrape_actions || '',
          scraper_notes: data.scraper_notes || ''
        });
      }
    } catch (error) {
      console.error('Error fetching scraper config:', error);
    }
  };

  // Intelligent Scraper: Update scraper configuration
  const updateScraperConfig = async () => {
    try {
      // Validate JSON for pre_scrape_actions
      if (scraperConfigForm.pre_scrape_actions.trim()) {
        try {
          JSON.parse(scraperConfigForm.pre_scrape_actions);
        } catch (e) {
          alert('Invalid JSON in Pre-Scrape Actions. Please check the format.');
          return;
        }
      }

      const response = await fetch(`${API_BASE_URL}/city/${encodeURIComponent(cityName)}/scraper_config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(scraperConfigForm)
      });
      const data = await response.json();
      
      if (data.error) {
        alert('Error updating scraper config: ' + data.error);
      } else {
        alert('✅ Scraper configuration updated successfully!');
        setEditingScraperConfig(false);
        fetchScraperConfig();
      }
    } catch (error) {
      console.error('Error updating scraper config:', error);
      alert('Failed to update scraper configuration');
    }
  };

  // Intelligent Scraper: Start editing scraper config
  const startEditingScraperConfig = () => {
    setScraperConfigForm({
      url: scraperConfig.url || '',
      manual: scraperConfig.manual || false,
      row_selector: scraperConfig.row_selector || '',
      cell_count: scraperConfig.cell_count || 4,
      is_dynamic: scraperConfig.is_dynamic || false,
      pre_scrape_actions: scraperConfig.pre_scrape_actions || '',
      scraper_notes: scraperConfig.scraper_notes || ''
    });
    setEditingScraperConfig(true);
  };

  const updateJobStatus = async (jobId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/update_job_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_id: jobId,
          status: newStatus,
          user_name: userName
        })
      });
      const data = await response.json();
      
      if (data.error) {
        alert('Error updating status: ' + data.error);
      } else {
        fetchCityJobs();
      }
    } catch (error) {
      console.error('Error updating job status:', error);
      alert('Failed to update status');
    }
  };

  const updateJobWorkType = async (jobId, newWorkType) => {
    try {
      const response = await fetch(`${API_BASE_URL}/update_work_type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_id: jobId,
          work_type: newWorkType
        })
      });
      const data = await response.json();
      
      if (data.error) {
        alert('Error updating work type: ' + data.error);
      } else {
        fetchCityJobs();
      }
    } catch (error) {
      console.error('Error updating work type:', error);
      alert('Failed to update work type');
    }
  };

  const addJournalEntry = async () => {
    if (!newJournalEntry.trim()) return;

    // Save user name to localStorage
    localStorage.setItem('rfq_user_name', userName);

    try {
      const response = await fetch(`${API_BASE_URL}/city_journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          city_name: cityName,
          entry_text: newJournalEntry,
          user_name: userName
        })
      });
      const data = await response.json();
      setJournalEntries([data.entry, ...journalEntries]);
      setNewJournalEntry('');
      console.log('Journal entry added:', data);
    } catch (error) {
      console.error('Error adding journal entry:', error);
    }
  };

  // Helper function for date parsing
  const parseDateSafely = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return null;
    try {
      let cleanedDate = dateStr
        .replace(/(\d+)(st|nd|rd|th)/g, '$1')
        .replace(/\s+MST|\s+PST|\s+EST|\s+CST/gi, '')
        .trim();
      const date = new Date(cleanedDate);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  // Helper function for due date styling
  const getDueDateStyle = (dueDateStr) => {
    const dueDate = parseDateSafely(dueDateStr);
    if (!dueDate) return {};
    
    const now = new Date();
    const diffDays = (dueDate - now) / (1000 * 60 * 60 * 24);
    
    if (diffDays < 0) return { color: 'red', fontWeight: 'bold' };
    if (diffDays <= 7) return { color: 'darkorange', fontWeight: 'bold' };
    if (diffDays <= 14) return { color: 'orange' };
    return {};
  };

  // Filter and sort jobs
  const filteredJobs = jobs.filter(job => {
    if (filters.workType !== 'all' && job.work_type !== filters.workType) return false;
    if (filters.userStatus !== 'all' && normalizeStatus(job.user_status) !== filters.userStatus) return false;
    if (filters.hideIgnored && normalizeStatus(job.user_status) === 'irrelevant') return false;
    return true;
  }).sort((a, b) => {
    switch (filters.sortBy) {
      case 'due_date':
        const dateA = parseDateSafely(a.due_date);
        const dateB = parseDateSafely(b.due_date);
        const now = new Date();
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        const futureA = dateA >= now;
        const futureB = dateB >= now;
        if (futureA && !futureB) return -1;
        if (!futureA && futureB) return 1;
        return dateA - dateB;
      case 'work_type':
        return (a.work_type || '').localeCompare(b.work_type || '');
      case 'date_added':
        const addedA = a.first_seen ? new Date(a.first_seen) : new Date(0);
        const addedB = b.first_seen ? new Date(b.first_seen) : new Date(0);
        return addedB - addedA;
      default:
        return 0;
    }
  });

  if (loading) {
    return <div className="loading">Loading city profile...</div>;
  }

  if (!cityData) {
    return <div className="error">City not found</div>;
  }

  // Check if this is a manual-only city
  const isManualOnly = cityData.manual || !cityData.is_active;

  return (
    <div className="city-profile-container">
      <div className="profile-header">
        <div className="d-flex gap-2">
          <button onClick={onBack} className="back-button">← Back to Cities</button>
          <button 
            onClick={() => window.print()} 
            className="back-button"
            title="Print or save as PDF"
          >
            🖨️ Print/PDF
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h1 style={{ margin: 0 }}>{cityData.name}</h1>
          {cityData.url && (
            <a 
              href={cityData.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm"
              style={{ whiteSpace: 'nowrap' }}
              title="Visit city's procurement website"
            >
              🔗 Visit Website
            </a>
          )}
        </div>
        <button 
          onClick={toggleTheme} 
          className="theme-toggle"
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          aria-label="Toggle theme"
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
      </div>

      <div className="profile-grid">
        {/* 1. Manual Entry Alert (if required) */}
        {isManualOnly && (
          <div className="profile-card full-width" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
            <h2 style={{ color: '#856404' }}>⚠️ Manual Entry Required</h2>
            <p style={{ color: '#856404', marginBottom: 0 }}>
              This city requires manual job entry. Please check their procurement website directly and use the "Add Job Manually" section below to add new opportunities.
            </p>
          </div>
        )}

        {/* 2. Active RFQs with Filters */}
        <div className="profile-card full-width" style={{ maxWidth: '95vw' }}>
          <h2>Active RFQs ({filteredJobs.length})</h2>
          
          {/* Filters - Using Single Source of Truth */}
          <div className="row mb-3">
            <div className="col-md-3">
              <label className="form-label">Work Type</label>
              <select 
                className="form-select form-select-sm"
                value={filters.workType}
                onChange={e => setFilters({...filters, workType: e.target.value})}
              >
                <option value="all">All Types</option>
                {WORK_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select 
                className="form-select form-select-sm"
                value={filters.userStatus}
                onChange={e => setFilters({...filters, userStatus: e.target.value})}
              >
                <option value="all">All Status</option>
                {JOB_STATUSES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Sort By</label>
              <select 
                className="form-select form-select-sm"
                value={filters.sortBy}
                onChange={e => setFilters({...filters, sortBy: e.target.value})}
              >
                <option value="due_date">Due Date</option>
                <option value="work_type">Work Type</option>
                <option value="date_added">Date Added</option>
              </select>
            </div>
            <div className="col-md-3">
              <div className="form-check" style={{ marginTop: '32px' }}>
                <input 
                  className="form-check-input"
                  type="checkbox"
                  checked={filters.hideIgnored}
                  onChange={e => setFilters({...filters, hideIgnored: e.target.checked})}
                  id="hideIgnoredCity"
                />
                <label className="form-check-label" htmlFor="hideIgnoredCity">
                  Hide Irrelevant
                </label>
              </div>
            </div>
          </div>

          {/* Jobs Table - Sprint 20: Refactored with DataTable */}
          {filteredJobs.length > 0 ? (
            <DataTable
              columns={RFQ_COLUMNS}
              data={filteredJobs}
              tableId={`city-jobs-${cityName}`}
              enableRowSelection={false}
              enableColumnResizing={true}
              enableColumnOrdering={true}
              enableFilters={false}
              enablePagination={false}
              meta={{
                updateUserStatus: updateJobStatus,
                updateWorkType: updateJobWorkType,
                onJobIdClick: onSelectJob, // Pass click handler to column
              }}
              className="city-jobs-table"
              defaultSorting={[{ id: 'due_date', desc: false }]}
            />
          ) : (
            <p className="text-muted">No active RFQs for this city</p>
          )}
        </div>

        {/* 3. City Contacts */}
        {cityContacts.length > 0 && (
          <div className="profile-card full-width">
            <h2>👥 Associated Contacts ({cityContacts.length})</h2>
            <p className="text-muted">Contacts linked to this city (e.g., City Employees, Agency Representatives)</p>
            
            <div className="row">
              {cityContacts.map((contact, idx) => (
                <div key={idx} className="col-md-4 mb-3">
                  <div className="card">
                    <div className="card-body">
                      <h6 className="card-title mb-2">{contact.name}</h6>
                      {contact.company_name && <p className="card-text mb-1"><small><strong>Company:</strong> {contact.company_name}</small></p>}
                      {contact.contact_type && <p className="card-text mb-1"><small><span className="badge bg-secondary">{contact.contact_type}</span></small></p>}
                      {contact.phone && <p className="card-text mb-1"><small><strong>Phone:</strong> <a href={`tel:${contact.phone}`}>{contact.phone}</a></small></p>}
                      {contact.email && <p className="card-text mb-1"><small><strong>Email:</strong> <a href={`mailto:${contact.email}`}>{contact.email}</a></small></p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. City Journal */}
        <div className="profile-card full-width">
          <h2>📝 City Journal</h2>
          <p className="text-muted">Track notes, patterns, contacts, and bidding history for this city.</p>
          
          {/* New Entry Form */}
          <div className="journal-entry-form mb-4">
            <div className="input-group mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Your name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                style={{ maxWidth: '200px' }}
              />
            </div>
            <textarea
              className="form-control mb-2"
              placeholder="Add a journal entry about this city (contacts, patterns, tips, etc.)..."
              value={newJournalEntry}
              onChange={(e) => setNewJournalEntry(e.target.value)}
              rows="4"
            ></textarea>
            <button
              className="btn btn-primary"
              onClick={addJournalEntry}
              disabled={!newJournalEntry.trim()}
            >
              Add Entry
            </button>
          </div>

          {/* Journal Entries List */}
          <div className="journal-entries">
            {journalEntries.length === 0 ? (
              <p className="text-muted">No journal entries yet. Add your first note above!</p>
            ) : (
              journalEntries.map((entry) => (
                <div key={entry.id} className="journal-entry">
                  <div className="journal-entry-header">
                    <strong>{entry.user_name}</strong>
                    <span className="text-muted">
                      {new Date(entry.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="journal-entry-text">
                    {entry.entry_text}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 4. City Links & Resources (Sprint 7) */}
        <div className="profile-card full-width">
          <h2>🔗 City Links & Resources</h2>
          <p className="text-muted">Store useful links for this city (master plan, budget, strategic plan, etc.)</p>
          
          <button 
            className="btn btn-primary mb-3" 
            onClick={() => editingLinkId ? cancelEditLink() : setShowAddLink(!showAddLink)}
          >
            {showAddLink ? '✖ Cancel' : '➕ Add Link'}
          </button>

          {showAddLink && (
            <div className="card mb-3 p-3">
              <div className="mb-2">
                <label className="form-label">Link Title *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., Master Plan 2024"
                  value={newLink.link_title}
                  onChange={(e) => setNewLink({...newLink, link_title: e.target.value})}
                />
              </div>
              <div className="mb-2">
                <label className="form-label">URL *</label>
                <input
                  type="url"
                  className="form-control"
                  placeholder="https://..."
                  value={newLink.link_url}
                  onChange={(e) => setNewLink({...newLink, link_url: e.target.value})}
                />
              </div>
              <div className="mb-2">
                <label className="form-label">Category</label>
                <select 
                  className="form-control"
                  value={newLink.link_type}
                  onChange={(e) => setNewLink({...newLink, link_type: e.target.value})}
                >
                  <option value="general">General</option>
                  <option value="master_plan">Master Plan</option>
                  <option value="budget">Budget</option>
                  <option value="cip">Capital Improvement Plan</option>
                  <option value="strategic">Strategic Plan</option>
                  <option value="procurement">Procurement Info</option>
                  <option value="contact">Contact Info</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label">Notes (optional)</label>
                <textarea
                  className="form-control"
                  rows="2"
                  placeholder="Additional notes about this link..."
                  value={newLink.notes}
                  onChange={(e) => setNewLink({...newLink, notes: e.target.value})}
                ></textarea>
              </div>
              <div>
                <button 
                  className="btn btn-success me-2" 
                  onClick={editingLinkId ? updateCityLink : addCityLink}
                >
                  {editingLinkId ? 'Update Link' : 'Save Link'}
                </button>
                {editingLinkId && (
                  <button className="btn btn-secondary" onClick={cancelEditLink}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          {cityLinks.length === 0 ? (
            <p className="text-muted">No links added yet. Click "Add Link" to get started!</p>
          ) : (
            <div className="list-group">
              {cityLinks.map((link) => (
                <div key={link.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        <span className="badge bg-secondary me-2">{link.link_type}</span>
                        <a href={link.link_url} target="_blank" rel="noopener noreferrer">
                          {link.link_title}
                        </a>
                      </h6>
                      {link.notes && <p className="mb-1 text-muted small">{link.notes}</p>}
                      <small className="text-muted">
                        Added: {new Date(link.created_at).toLocaleDateString()}
                      </small>
                    </div>
                    <div>
                      <button 
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => startEditLink(link)}
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteCityLink(link.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 5. Add Job Manually (Sprint 7) */}
        <div className="profile-card full-width">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h2 style={{ margin: 0 }}>➕ Add Job Manually</h2>
            {cityData.url && (
              <a 
                href={cityData.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm"
                title="Open city's procurement website in new tab"
              >
                🔗 Open Website
              </a>
            )}
          </div>
          <p className="text-muted">
            Manually add jobs from this city's procurement website. Perfect for manual-only cities like State of Arizona.
          </p>
          
          <button 
            className="btn btn-primary mb-3" 
            onClick={() => setShowManualJobEntry(!showManualJobEntry)}
          >
            {showManualJobEntry ? '✖ Cancel' : '➕ Add Manual Job'}
          </button>

          {showManualJobEntry && (
            <div className="card p-3">
              <div className="mb-2">
                <label className="form-label">Job Details * (paste from website)</label>
                <textarea
                  className="form-control"
                  rows="10"
                  placeholder={`Paste job details here. Include:\n- RFP/RFQ number\n- Title\n- Due date\n- Any other relevant information\n\nExample:\nRFP #2024-123 - Water System Improvements\nDue Date: 12/31/2024\nBudget: $500,000`}
                  value={manualJobText}
                  onChange={(e) => setManualJobText(e.target.value)}
                ></textarea>
              </div>
              
              <div className="alert alert-warning" style={{ marginBottom: '15px' }}>
                <strong>⚠️ Important:</strong> Paste <strong>ONE job at a time</strong> and make sure you're on the <strong>correct city profile page</strong>. 
                The job will be added to <strong>{cityData.name}</strong>.
                <br/>
                <small className="text-muted">
                  Example: For an ADOT job, go to "State of Arizona" profile page first, then paste the job details there.
                </small>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Source URL (optional)</label>
                <input
                  type="url"
                  className="form-control"
                  placeholder="https://..."
                  value={manualJobUrl}
                  onChange={(e) => setManualJobUrl(e.target.value)}
                />
              </div>
              <div className="alert alert-info">
                <strong>💡 Tip:</strong> The system will automatically extract the RFP number, title, and due date from your text.
              </div>
              <button 
                className="btn btn-success" 
                onClick={addManualJob}
                disabled={!manualJobText.trim()}
              >
                Save Job
              </button>
            </div>
          )}
        </div>

        {/* 6. Scraping Configuration & History */}
        <div className="profile-card">
          <h2>🤖 Scraping Configuration</h2>
          <p className="text-muted">Configure how the scraper handles this city's website</p>
          
          <button 
            className="btn btn-secondary mb-3" 
            onClick={() => setShowScraperConfig(!showScraperConfig)}
          >
            {showScraperConfig ? '▼ Hide Details' : '▶ Show Details'}
          </button>

          {showScraperConfig && scraperConfig && (
            <div className="card p-3">
              <div className="config-item">
                <label>Status:</label>
                <span className={`badge ${scraperConfig.manual ? 'bg-warning' : 'bg-success'}`}>
                  {scraperConfig.manual ? 'Manual Only' : 'Automated'}
                </span>
              </div>
              <div className="config-item">
                <label>URL:</label>
                <a href={scraperConfig.url} target="_blank" rel="noopener noreferrer">
                  {scraperConfig.url}
                </a>
              </div>
              <div className="config-item">
                <label>Dynamic Content:</label>
                <span>{scraperConfig.is_dynamic ? 'Yes' : 'No'}</span>
              </div>
              <div className="config-item">
                <label>Row Selector:</label>
                <code>{scraperConfig.row_selector || 'N/A'}</code>
              </div>
              <div className="config-item">
                <label>Cell Count:</label>
                <span>{scraperConfig.cell_count}</span>
              </div>
              {scraperConfig.pre_scrape_actions && (
                <div className="config-item">
                  <label>Pre-Scrape Actions:</label>
                  <code className="d-block">{scraperConfig.pre_scrape_actions}</code>
                </div>
              )}
              {scraperConfig.scraper_notes && (
                <div className="alert alert-info mt-2">
                  <strong>📝 Notes:</strong> {scraperConfig.scraper_notes}
                </div>
              )}

              <hr />
              
              <button 
                className="btn btn-primary" 
                onClick={startEditingScraperConfig}
              >
                ✏️ Edit Scraper Config
              </button>

              {editingScraperConfig && (
                <div className="card mt-3 p-3 bg-light">
                  <h5>⚙️ Edit Scraper Configuration</h5>
                  <p className="text-muted small">⚠️ Advanced: Only edit if you understand scraper settings</p>

                  <div className="mb-3">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={scraperConfigForm.manual}
                        onChange={(e) => setScraperConfigForm({...scraperConfigForm, manual: e.target.checked})}
                      />
                      Manual Mode (skip automated scraping)
                    </label>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Website URL</label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://city.gov/procurement"
                      value={scraperConfigForm.url}
                      onChange={(e) => setScraperConfigForm({...scraperConfigForm, url: e.target.value})}
                    />
                    <small className="text-muted">The URL to the city's procurement/RFQ page</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Row Selector (CSS)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="tbody tr"
                      value={scraperConfigForm.row_selector}
                      onChange={(e) => setScraperConfigForm({...scraperConfigForm, row_selector: e.target.value})}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Cell Count</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="20"
                      value={scraperConfigForm.cell_count}
                      onChange={(e) => setScraperConfigForm({...scraperConfigForm, cell_count: parseInt(e.target.value)})}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={scraperConfigForm.is_dynamic}
                        onChange={(e) => setScraperConfigForm({...scraperConfigForm, is_dynamic: e.target.checked})}
                      />
                      Dynamic Content (uses JavaScript/AJAX)
                    </label>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Pre-Scrape Actions (JSON)</label>
                    <textarea
                      className="form-control font-monospace"
                      rows="5"
                      placeholder='[{"type": "click", "selector": ".expand-btn", "wait": 2, "all": true}]'
                      value={scraperConfigForm.pre_scrape_actions}
                      onChange={(e) => setScraperConfigForm({...scraperConfigForm, pre_scrape_actions: e.target.value})}
                    ></textarea>
                    <small className="text-muted">
                      Examples: click expand buttons, wait for elements, scroll page. 
                      <a href="https://github.com/yourusername/rfq-tracker/wiki/Pre-Scrape-Actions" target="_blank" rel="noopener noreferrer">
                        See docs
                      </a>
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Scraper Notes</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Notes about this city's scraping challenges or quirks..."
                      value={scraperConfigForm.scraper_notes}
                      onChange={(e) => setScraperConfigForm({...scraperConfigForm, scraper_notes: e.target.value})}
                    ></textarea>
                  </div>

                  <div>
                    <button 
                      className="btn btn-success me-2" 
                      onClick={updateScraperConfig}
                    >
                      ✅ Save Config
                    </button>
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => setEditingScraperConfig(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="profile-card">
          <h2>Recent Scrape History</h2>
          {cityData.scrape_history && cityData.scrape_history.length > 0 ? (
            <div className="history-list">
              {cityData.scrape_history.map((scrape, index) => (
                <div key={index} className="history-item">
                  <div className="history-date">{scrape.date}</div>
                  <div className="history-details">
                    <span className="history-count">{scrape.count} RFQs</span>
                    <span className="history-status">{scrape.status}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="placeholder-data">No scrape history available yet</p>
          )}
        </div>

        {/* 7. Demographics */}
        <div className="profile-card">
          <h2>Demographics</h2>
          <div className="demo-item">
            <label>Population (2025):</label>
            <span className="placeholder-data">{cityData.population_current || '—'}</span>
          </div>
          <div className="demo-item">
            <label>Population (2020):</label>
            <span className="placeholder-data">{cityData.population_2020 || '—'}</span>
          </div>
          <div className="demo-item">
            <label>Expected Growth:</label>
            <span className="placeholder-data">{cityData.expected_growth || '—'}</span>
          </div>
          <div className="demo-item">
            <label>Avg. Household Income:</label>
            <span className="placeholder-data">
              {cityData.avg_household_income ? `$${cityData.avg_household_income.toLocaleString()}` : '—'}
            </span>
          </div>
          <div className="demo-item">
            <label>Drive Time from Tempe:</label>
            <span className="placeholder-data">{cityData.drive_time || '—'}</span>
          </div>
        </div>

        {/* 8. Contact & Bonds */}
        <div className="profile-card">
          <h2>Contact & Bonds Information</h2>
          <div className="demo-item">
            <label>Primary Contact:</label>
            <span className="placeholder-data">{cityData.contact_name || '—'}</span>
          </div>
          <div className="demo-item">
            <label>Contact Email:</label>
            <span className="placeholder-data">{cityData.contact_email || '—'}</span>
          </div>
          <div className="demo-item">
            <label>Contact Phone:</label>
            <span className="placeholder-data">{cityData.contact_phone || '—'}</span>
          </div>
          <div className="demo-item">
            <label>Bonds (Upcoming Election):</label>
            <span className="placeholder-data">
              {cityData.bonds_upcoming !== null ? (cityData.bonds_upcoming ? 'Yes' : 'No') : '—'}
            </span>
          </div>
          <div className="demo-item">
            <label>Bonds (Last Election):</label>
            <span className="placeholder-data">
              {cityData.bonds_last !== null ? (cityData.bonds_last ? 'Yes' : 'No') : '—'}
            </span>
          </div>
          <div className="demo-item">
            <label>Bond Amount (Last):</label>
            <span className="placeholder-data">{cityData.bond_amount || '—'}</span>
          </div>
        </div>

      </div>
    </div>
  );
}

export default CityProfile;
